/* eslint-disable sonarjs/cognitive-complexity */
import {env} from '@/services/env.service';
import { type BaseConfig, Theme } from '@odin/services';
import { cloneDeep } from 'lodash-es';
import { WORKFLOW } from '@/consts/const.enum';

export interface AppConfig extends BaseConfig {
  env?: string; // production, staging, development, testing (for test runners)...
  baseUrl: string;
  apiUrl: string;
  assetUrl: string;
  loginUrl: string;
  logoutUrl: string;
  supportUrl: string;
  invoiceUrl: string; // deprecated
  baseInvoiceUrl: string; // will be combined with invoice ID to get full invoiceUrl
  webUrl: string; // homepage
  webAdminUrl: string;
  dedicatedServersUrl: string;
  monitoringUrl: string;
  managementUrl: string;
  transferUrl: string;
  hostingsUrl: string;
  tosUrl: string; // TOS
  company: string; // company (aka market) id, for example sk-ws for websupport Slovakia
  facebookId?: string; // tracking number
  currency: any; // ISO 4217 currency code
  appRootElm?: string; // optionally change SPA root element
  companyLangShort?: string;
  language?: string;
  migrationInfoUrl?: string; // only if cart contains product with possibility of migration
  billingProfileCountLimit?: number; // limit number of billing profiles
  domainProfileCountLimit?: number; // limit number of domain profiles
  workflow?: number; // business workflow enum
  providedConfig?: any; // input values provided by BE (root values might be changes bellow)
  freeOnlyInitial?: boolean; // show/hide only free domains initially
  theme: Theme;
  userId?: number;
  redirectedFrom?: string;
  parentApplicationType?: ParentApplicationType
}

export type ParentApplicationType = 'admin' | 'web';

export const config = {} as AppConfig;

// TODO: move to BE
const countryToBounds: Record<string, AppConfig['bounds']> = {
  SK: {
    east: 16.8799829444,
    south: 47.7584288601,
    west: 22.5581376482,
    north: 49.5715740017,
  },
  CZ: {
    east: 12.2401111182,
    south: 48.5553052842,
    west: 18.8531441586,
    north: 51.1172677679,
  },
  HU: {
    east: 16.2022982113,
    south: 45.7594811061,
    west: 22.710531447,
    north: 48.6238540716,
  },
  SE: {
    east: 11.0273686052,
    south: 55.3617373725,
    west: 23.9033785336,
    north: 69.1062472602,
  },
};

const isTestEnv = env.MODE === 'testing';
let swedishWorkflow;

if (typeof window !== 'undefined') {
  let providedConfig = {};

  if (!isTestEnv) {
    const mcElm = document.getElementById('main-content');
    if (!mcElm) {
      throw new Error('#main-content element not found');
    }
    const paramsAttr = (mcElm as any).attributes['data-params'];
    if (!paramsAttr) {
      throw new Error('data-params attribute on #main-content not found');
    }
    providedConfig = paramsAttr.value && JSON.parse(paramsAttr.value);
    if (!providedConfig) {
      throw new Error('App configuration not found!');
    }
  } else {
    providedConfig = { company: 'sk-ws' };
  }

  Object.assign(config, { ...cloneDeep(providedConfig), providedConfig });

  // Always use current port for DEV api
  if (config.apiUrl?.startsWith('http://localhost:')) {
    config.apiUrl = config.apiUrl.replace('3000', location.port);
  }

  // config.env = config.env ?? 'production';
  // temporary parsing url to get proper env until will be fixed on BE side
  // ref: https://loopiagroup.atlassian.net/browse/PNO-2754
  const url = window?.location?.href || '';
  if (url.includes('.odin')) {
    config.env = 'development';
  } else if (url.includes('.staging.') || url.includes('.feature.')) {
    config.env = 'staging';
  } else {
    config.env = 'production';
  }

  config.companyCountry =
    config.companyCountry ?? config.company.slice(0, 2).toUpperCase();
  // ensure two letters (fixes abnormalities like "HU-GYT")
  config.companyCountry = config.companyCountry.slice(0, 2).toUpperCase();
  config.bounds =
    config.bounds ??
    countryToBounds[config.companyCountry] ??
    countryToBounds.SK;

  config.theme = config.theme ?? Theme.CARY;
  config.themeOptions = {
    ...config.themeOptions,
    spinner: true,
    btnDynamicIconSize: true,
  };
  config.currency = config.currency ?? 'EUR';

  config.languages = config.languages ?? {
    'sk-SK': 'Slovensky',
    'en-US': 'English',
  };
  const companyLangLong = Object.keys(config.languages)[0];
  config.language = config.language ?? companyLangLong;
  config.companyLangShort =
    config.companyLangShort ?? companyLangLong.slice(0, 2); // two letter format

  config.workflow = parseInt((config.workflow ?? WORKFLOW.STANDARD) as any);
  swedishWorkflow = config.workflow === WORKFLOW.SWEDISH;

  config.billingProfileCountLimit = !isNaN(
    parseInt(config.billingProfileCountLimit as any)
  )
    ? parseInt(config.billingProfileCountLimit as any)
    : swedishWorkflow
      ? 1
      : undefined;
  config.domainProfileCountLimit = !isNaN(
    parseInt(config.domainProfileCountLimit as any)
  )
    ? parseInt(config.domainProfileCountLimit as any)
    : swedishWorkflow
      ? 1
      : undefined;

  config.validationApiUrl = config.validationApiUrl ?? config.apiUrl;

  config.freeOnlyInitial = false;

  // temprary until BE will send this parameter (TODO: remove)
  config.transferUrl =
    config.transferUrl ??
    'https://www.websupport.sk/support/kb/zmena-registratora/';

  // ensure consistent urls
  const entries = Object.entries(config) as [keyof AppConfig, any][];
  entries.forEach(([key, value]: [string, any]) => {
    if (
      key.slice(-3) === 'Url' &&
      typeof value === 'string' &&
      value.slice(-1) === '/'
    ) {
      (config as any)[key] = value.slice(0, -1);
    }
  });

  if (!isTestEnv) {
    (window as any).__WS_New_Order__ = config;
  }

  // @ts-ignore
  window.basePath = config.assetUrl;
}

export const isStandardWorkflow = config.workflow === WORKFLOW.STANDARD;
export const isSwedishWorkflow = swedishWorkflow;

export default config;
